"use client";

import { c as _c } from "react/compiler-runtime";
import type React from "react";
import Link from "next/link";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWater } from "@fortawesome/free-solid-svg-icons";
import { FallbackImage } from "@/components/common/FallbackImage";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { getWaveDescriptionPreviewText } from "@/helpers/waves/waveDescriptionPreview";
import { useProfileWave } from "@/hooks/useProfileWave";
import { useWaveById } from "@/hooks/useWaveById";
import { useWaveCurationPreviewDrops } from "@/hooks/useWaveCurationPreviewDrops";
import { useWaveCurations } from "@/hooks/waves/useWaveCurations";
import { CurationPreviewShell } from "./curation-preview/CurationPreviewShell";
import { PreviewTile } from "./curation-preview/CurationPreviewTiles";
import { getPreviewItems } from "./curation-preview/dropClassification";
import type { CurationWavePreviewCardProps, CurationWavePreviewCardVariant } from "./curation-preview/types";
import { getTrimmedText, getWaveAuthor, getWaveHref, PREVIEW_DROPS_FETCH_LIMIT } from "./curation-preview/utils";
const getBannerBackground = ({
  banner1,
  banner2
}: {
  readonly banner1?: string | null | undefined;
  readonly banner2?: string | null | undefined;
}): string | null => {
  const firstColor = getTrimmedText(banner1);
  const secondColor = getTrimmedText(banner2);
  if (firstColor && secondColor) {
    return `linear-gradient(60deg, ${firstColor} 0%, ${secondColor} 100%)`;
  }
  return null;
};
const WavePreviewPicture: React.FC<{
  readonly picture: string | null | undefined;
  readonly sizeClassName: string;
  readonly iconClassName: string;
  readonly imageSize: string;
}> = t0 => {
  const $ = _c(7);
  const {
    picture,
    sizeClassName,
    iconClassName,
    imageSize
  } = t0;
  const t1 = `${sizeClassName} tw-relative tw-flex-shrink-0 tw-overflow-hidden tw-rounded-full tw-bg-[#1A1A20] tw-shadow-sm tw-ring-1 tw-ring-white/[0.05]`;
  let t2;
  if ($[0] !== iconClassName || $[1] !== imageSize || $[2] !== picture) {
    t2 = picture ? <FallbackImage primarySrc={getScaledImageUri(picture, ImageScale.W_AUTO_H_50)} fallbackSrc={picture} alt="" fill={true} sizes={imageSize} className="tw-object-cover" /> : <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center"><FontAwesomeIcon icon={faWater} className={`${iconClassName} tw-text-white/30`} aria-hidden="true" /></div>;
    $[0] = iconClassName;
    $[1] = imageSize;
    $[2] = picture;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const CurationPreviewBody: React.FC<{
  readonly hasBannerCover: boolean;
  readonly variant: CurationWavePreviewCardVariant;
  readonly children: React.ReactNode;
}> = t0 => {
  const $ = _c(6);
  const {
    hasBannerCover,
    variant,
    children
  } = t0;
  if (hasBannerCover) {
    let t1;
    if ($[0] !== children) {
      t1 = <div className="tw-relative tw-z-10 -tw-mt-3 tw-px-5 tw-pb-4">{children}</div>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (variant === "sheet") {
    let t1;
    if ($[2] !== children) {
      t1 = <div className="tw-px-4 tw-pb-4 tw-pt-6">{children}</div>;
      $[2] = children;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t1;
  if ($[4] !== children) {
    t1 = <div className="tw-px-4 tw-pb-4 tw-pt-4">{children}</div>;
    $[4] = children;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
export const CurationWavePreviewCard: React.FC<CurationWavePreviewCardProps> = t0 => {
  const $ = _c(52);
  const {
    waveId,
    profileIdentity,
    fallbackName,
    fallbackPfp,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "hovercard" : t1;
  let t2;
  if ($[0] !== profileIdentity) {
    t2 = getTrimmedText(profileIdentity);
    $[0] = profileIdentity;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const normalizedProfileIdentity = t2;
  const {
    wave,
    isError: isWaveError
  } = useWaveById(waveId);
  const resolvedWave = wave?.id === waveId ? wave : undefined;
  const t3 = normalizedProfileIdentity !== null;
  let t4;
  if ($[2] !== normalizedProfileIdentity || $[3] !== t3) {
    t4 = {
      identity: normalizedProfileIdentity,
      enabled: t3
    };
    $[2] = normalizedProfileIdentity;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    data: profileWave,
    isError: isProfileWaveError
  } = useProfileWave(t4);
  const hasResolvedProfileWave = normalizedProfileIdentity === null || profileWave !== undefined || isProfileWaveError;
  const selectedProfileCurationId = profileWave?.profile_wave_id === waveId ? getTrimmedText(profileWave.profile_curation_id) : null;
  const shouldLoadFallbackCurations = hasResolvedProfileWave && selectedProfileCurationId === null;
  let t5;
  if ($[5] !== shouldLoadFallbackCurations || $[6] !== waveId) {
    t5 = {
      waveId,
      enabled: shouldLoadFallbackCurations
    };
    $[5] = shouldLoadFallbackCurations;
    $[6] = waveId;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    data: t6,
    isError: isCurationsError,
    isFetched: areCurationsFetched
  } = useWaveCurations(t5);
  const curations = t6 === undefined ? [] : t6;
  const fallbackCurationId = shouldLoadFallbackCurations ? curations.at(0)?.id ?? null : null;
  const curationId = selectedProfileCurationId ?? fallbackCurationId;
  const canFetchPreviewDrops = hasResolvedProfileWave && curationId !== null && resolvedWave !== undefined;
  const {
    drops,
    isError: isDropsError,
    isFetched: areDropsFetched
  } = useWaveCurationPreviewDrops({
    wave: resolvedWave ?? null,
    curationId,
    pageSize: PREVIEW_DROPS_FETCH_LIMIT,
    enabled: canFetchPreviewDrops
  });
  const isProfileWaveUnresolved = normalizedProfileIdentity !== null && profileWave === undefined && !isProfileWaveError;
  const areFallbackCurationsUnresolved = shouldLoadFallbackCurations && !areCurationsFetched && !isCurationsError;
  const areWaveDetailsUnresolved = curationId !== null && resolvedWave === undefined && !isWaveError;
  const arePreviewDropsUnresolved = canFetchPreviewDrops && !areDropsFetched && !isDropsError;
  const isPreviewPending = isProfileWaveUnresolved || areFallbackCurationsUnresolved || areWaveDetailsUnresolved || arePreviewDropsUnresolved;
  let t7;
  let waveName;
  if ($[8] !== drops || $[9] !== fallbackName || $[10] !== fallbackPfp || $[11] !== resolvedWave?.name || $[12] !== resolvedWave?.picture) {
    const firstDropWave = drops.at(0)?.wave;
    waveName = getTrimmedText(resolvedWave?.name) ?? getTrimmedText(fallbackName) ?? getTrimmedText(firstDropWave?.name) ?? "Featured wave";
    t7 = getTrimmedText(resolvedWave?.picture) ?? getTrimmedText(fallbackPfp) ?? getTrimmedText(firstDropWave?.picture);
    $[8] = drops;
    $[9] = fallbackName;
    $[10] = fallbackPfp;
    $[11] = resolvedWave?.name;
    $[12] = resolvedWave?.picture;
    $[13] = t7;
    $[14] = waveName;
  } else {
    t7 = $[13];
    waveName = $[14];
  }
  const wavePicture = t7;
  let t8;
  if ($[15] !== resolvedWave) {
    t8 = getWaveAuthor(resolvedWave);
    $[15] = resolvedWave;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const author = t8;
  let t9;
  if ($[17] !== resolvedWave) {
    t9 = getWaveDescriptionPreviewText(resolvedWave);
    $[17] = resolvedWave;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const description = t9;
  let t10;
  if ($[19] !== drops) {
    t10 = getPreviewItems(drops);
    $[19] = drops;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const previewItems = t10;
  const waveHref = getWaveHref({
    waveId,
    wave: resolvedWave,
    curationId
  });
  const t11 = resolvedWave?.author.banner1_color;
  const t12 = resolvedWave?.author.banner2_color;
  let t13;
  if ($[21] !== t11 || $[22] !== t12) {
    t13 = getBannerBackground({
      banner1: t11,
      banner2: t12
    });
    $[21] = t11;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const bannerBackground = t13;
  const hasBannerCover = bannerBackground !== null;
  let t14;
  if ($[24] !== bannerBackground || $[25] !== hasBannerCover || $[26] !== variant) {
    t14 = hasBannerCover && <div className={variant === "sheet" ? "tw-relative tw-h-[68px] tw-w-full tw-overflow-hidden" : "tw-relative tw-h-14 tw-w-full tw-overflow-hidden"} aria-hidden="true"><div className="tw-absolute tw-inset-0 tw-opacity-85" style={{
        background: bannerBackground
      }} /><div className="tw-absolute tw-inset-0 tw-bg-[radial-gradient(circle_at_20%_10%,rgba(255,255,255,0.18),transparent_34%),linear-gradient(to_top,#131316_0%,rgba(19,19,22,0.42)_48%,rgba(19,19,22,0.08)_100%)]" /></div>;
    $[24] = bannerBackground;
    $[25] = hasBannerCover;
    $[26] = variant;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== author || $[29] !== hasBannerCover || $[30] !== waveName || $[31] !== wavePicture) {
    t15 = hasBannerCover ? <div className="tw-mb-3 tw-flex tw-items-center tw-gap-2.5"><WavePreviewPicture picture={wavePicture} sizeClassName="tw-h-10 tw-w-10" iconClassName="tw-h-4 tw-w-4" imageSize="40px" /><div className="tw-min-w-0 tw-flex-1"><div className="tw-line-clamp-2 tw-text-base tw-font-bold tw-leading-[1.15] tw-text-zinc-100">{waveName}</div>{author && <div className="tw-mt-1 tw-truncate tw-text-xs tw-font-medium tw-text-zinc-400">@{author}</div>}</div></div> : <div className="tw-flex tw-items-center tw-gap-3"><WavePreviewPicture picture={wavePicture} sizeClassName="tw-h-10 tw-w-10" iconClassName="tw-h-4 tw-w-4" imageSize="40px" /><div className="tw-min-w-0 tw-flex-1"><div className="tw-line-clamp-2 tw-text-base tw-font-bold tw-leading-[1.15] tw-text-zinc-100">{waveName}</div>{author && <div className="tw-mt-1 tw-truncate tw-text-xs tw-font-medium tw-text-zinc-400">@{author}</div>}</div></div>;
    $[28] = author;
    $[29] = hasBannerCover;
    $[30] = waveName;
    $[31] = wavePicture;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== description) {
    t16 = description && <p className="tw-mb-0 tw-mt-3 tw-line-clamp-2 tw-pr-1 tw-text-xs tw-font-medium tw-leading-[1.45] tw-text-zinc-300">{description}</p>;
    $[33] = description;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== isPreviewPending || $[36] !== previewItems) {
    t17 = <PreviewContent isPending={isPreviewPending} previewItems={previewItems} />;
    $[35] = isPreviewPending;
    $[36] = previewItems;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== hasBannerCover || $[39] !== t15 || $[40] !== t16 || $[41] !== t17 || $[42] !== variant) {
    t18 = <CurationPreviewBody hasBannerCover={hasBannerCover} variant={variant}>{t15}{t16}{t17}</CurationPreviewBody>;
    $[38] = hasBannerCover;
    $[39] = t15;
    $[40] = t16;
    $[41] = t17;
    $[42] = variant;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <ArrowRightIcon className="tw-h-3.5 tw-w-3.5 tw-transition-transform tw-duration-300 tw-ease-out desktop-hover:group-hover/open-wave:tw-translate-x-0.5" aria-hidden="true" />;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== waveHref) {
    t20 = <div className="tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/[0.06] tw-px-5 tw-py-3"><Link href={waveHref} prefetch={false} className="tw-group/open-wave tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[13px] tw-font-bold tw-text-primary-400 tw-no-underline tw-transition-colors tw-duration-300 desktop-hover:hover:tw-text-primary-300">Open wave{t19}</Link></div>;
    $[45] = waveHref;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== t14 || $[48] !== t18 || $[49] !== t20 || $[50] !== variant) {
    t21 = <CurationPreviewShell variant={variant}>{t14}{t18}{t20}</CurationPreviewShell>;
    $[47] = t14;
    $[48] = t18;
    $[49] = t20;
    $[50] = variant;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  return t21;
};
const PreviewContent: React.FC<{
  readonly isPending: boolean;
  readonly previewItems: ReturnType<typeof getPreviewItems>;
}> = t0 => {
  const $ = _c(5);
  const {
    isPending,
    previewItems
  } = t0;
  if (previewItems.length > 0) {
    let t1;
    if ($[0] !== previewItems) {
      t1 = previewItems.map(_temp);
      $[0] = previewItems;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <div className="tw-mt-4 tw-columns-2 tw-gap-2">{t1}</div>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (isPending) {
    let t1;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="tw-mt-4 tw-flex tw-w-full tw-items-center tw-justify-start tw-gap-2 tw-py-1.5 tw-text-left tw-text-xs tw-text-iron-400" role="status" aria-live="polite"><svg className="tw-h-3.5 tw-w-3.5 tw-flex-shrink-0 tw-animate-spin tw-text-iron-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg><span>Loading...</span></div>;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  return null;
};
function _temp(item) {
  return <PreviewTile key={item.key} item={item} />;
}